import React from 'react'
import { Eye } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { FeedbackFocus } from '@/hooks/useProductConfig'

interface FeedbackFocusSelectProps {
  value: FeedbackFocus
  onChange: (value: FeedbackFocus) => void
  showLabel?: boolean
}

export const TimeWindow = {
  THIRTY_DAYS: '30d',
  NINETY_DAYS: '90d',
  PAST_YEAR: '1y',
  ALL_TIME: 'all-time',
} as const

export type TimeWindow = (typeof TimeWindow)[keyof typeof TimeWindow]

const FeedbackFocusSelect: React.FC<FeedbackFocusSelectProps> = ({ value, onChange, showLabel = false }) => {
  const { isDarkMode } = useTheme()

  const feedbackFocusOptions = [
    { value: TimeWindow.THIRTY_DAYS, label: 'Past 30 Days' },
    { value: TimeWindow.NINETY_DAYS, label: 'Past 90 Days' },
    { value: TimeWindow.PAST_YEAR, label: 'Past Year' },
    { value: TimeWindow.ALL_TIME, label: 'All Time' },
  ]

  const getFeedbackFocusLabel = (value: TimeWindow) => {
    return feedbackFocusOptions.find((option) => option.value === value)?.label || 'Past 90 Days'
  }

  return (
    <div className={`flex ${showLabel ? 'flex-col gap-1.5' : 'items-center'}`}>
      {showLabel && (
        <label htmlFor="feedbackFocus" className="text-sm font-medium">
          Feedback Focus
        </label>
      )}
      <Select value={value} onValueChange={(val) => onChange(val as FeedbackFocus)}>
        <SelectTrigger
          id="feedbackFocus"
          className={`w-full ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}
        >
          <Eye className={`w-4 h-4 mr-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          <SelectValue placeholder={getFeedbackFocusLabel(value as TimeWindow)} />
        </SelectTrigger>
        <SelectContent className={isDarkMode ? 'bg-gray-800 border-gray-700' : ''}>
          {feedbackFocusOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

export default FeedbackFocusSelect
